import { Routes } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { AccessConstants } from './core/constants/access-constants';
import { RouteConstants } from './core/constants/routes';
import { UserRole } from './core/enums/users/role';
import { AuthenticatedUserStatusGuard } from './core/guards/authenticated-user-status/authenticated-user-status.guard';
import { BlockNavigationIfChangeGuard } from './core/guards/block-navigation-if-change/block-navigation-if-change.guard';
import { BlockNavigationIfPreferredPaymentMethodRequirementUnsatisfiedGuard } from './core/guards/block-navigation-if-preferred-payment-method-requirement-unsatisfied/block-navigation-if-preferred-payment-method-requirement-unsatisfied.guard';
import { GuardDataKeys } from './core/guards/constants/guard-constants';
import { CoordinationAuthGuard } from './core/guards/coordination-auth/coordination-auth.guard';
import { GeneralAuthGuardFn } from './core/guards/general-auth-guard';
import { GoPageCanActivateGuards } from './core/guards/go-page-can-activate-guards';
import { HasVisitedGoOverviewGuard } from './core/guards/has-visited-go-overview/has-visited-go-overview-guard';
import { SignupGuard } from './core/guards/signup-guard';
import { UserRoleAuthGuard } from './core/guards/user-role-auth/user-role-auth.guard';
import { UserStatusGuard } from './core/guards/user-status/user-status.guard';
import { UserStatus } from './core/models/user-status';
import { directEntryPageResolvers } from './core/resolver/direct-entry-page-resolvers/direct-entry-page-resolvers';
import { ContactLookupComponent } from './modules/application/contact-lookup/contact-lookup.component';
import { CoordinationParametersComponent } from './modules/application/coordination/coordination-parameters/coordination-parameters.component';
import { CoordinationResultsComponent } from './modules/application/coordination/coordination-results/coordination-results.component';
import { ApplicantInfoComponent } from './modules/application/direct-entry/applicant-info/applicant-info.component';
import { AttachmentsAndWaiversComponent } from './modules/application/direct-entry/attachments-and-waivers/attachments-and-waivers.component';
import { CertificationInfoComponent } from './modules/application/direct-entry/certification-info/certification-info.component';
import { FeesWaiversExemptionsComponent } from './modules/application/direct-entry/fees-waivers-exemptions/fees-waivers-exemptions.component';
import { GeneralInfoComponent } from './modules/application/direct-entry/general-info/general-info.component';
import { RegulatoryInfoComponent } from './modules/application/direct-entry/regulatory-info/regulatory-info.component';
import { ScheduleHAdditionalInformationComponent } from './modules/application/direct-entry/schedule-h-additional-information/schedule-h-additional-information.component';
import { ScheduleKComponent } from './modules/application/direct-entry/schedule-k/schedule-k.component';
import { SchedulesDAndHComponent } from './modules/application/direct-entry/schedules-d-and-h/schedules-d-and-h.component';
import { DirectEntryStepPaths } from './modules/application/direct-entry/shared/constants/direct-entry-steps';
import { SubmitToEwaComponent } from './modules/application/direct-entry/submit-to-ewa/submit-to-ewa.component';
import { FrnFilingDetailComponent } from './modules/application/frn-filing/frn-filing-detail/frn-filing-detail.component';
import { FrnFilingTypeComponent } from './modules/application/frn-filing/frn-filing-type/frn-filing-type.component';
import { GoAboutComponent } from './modules/application/go/go-about/go-about.component';
import { GoCompleteComponent } from './modules/application/go/go-complete/go-complete.component';
import { GoConfirmParametersComponent } from './modules/application/go/go-confirm-parameters/go-confirm-parameters.component';
import { GoFeesComponent } from './modules/application/go/go-fees/go-fees.component';
import { GoFrequencyResultsComponent } from './modules/application/go/go-frequency-results/go-frequency-results.component';
import { GoHelpComponent } from './modules/application/go/go-help/go-help-component';
import { GoOverviewComponent } from './modules/application/go/go-overview/go-overview.component';
import { GoParametersComponent } from './modules/application/go/go-parameters/go-parameters.component';
import { GoPurposeOfFilingComponent } from './modules/application/go/go-purpose-of-filing/go-purpose-of-filing.component';
import { GoSearchProcessingComponent } from './modules/application/go/go-search-processing/go-search-processing.component';
import { GoBaseCoordinationLayoutComponent } from './modules/application/go/shared/layouts/go-base-coordination-layout/go-base-coordination-layout.component';
import { GoBaseLayoutComponent } from './modules/application/go/shared/layouts/go-base-layout/go-base-layout.component';
import { GoPageApplicationHandedOverResolver } from './modules/application/go/shared/resolvers/go-page-application-handed-over-resolver';
import { GoPageApplicationStateRefreshResolver } from './modules/application/go/shared/resolvers/go-page-application-state-refresh-resolver';
import { CustomerPaymentDashboardComponent } from './modules/application/orders/customer-payment-dashboard/customer-payment-dashboard.component';
import { OrderDashboardContainerComponent } from './modules/application/orders/order-dashboard-container/order-dashboard-container.component';
import { OrderDetailsComponent } from './modules/application/orders/order-details/order-details.component';
import { PaymentDetailsComponent } from './modules/application/orders/payment-details/payment-details.component';
import { PurposeOfFilingComponent } from './modules/application/purpose-of-filing/purpose-of-filing.component';
import { QuickStartComponent } from './modules/application/quick-start/quick-start.component';
import { ApplicationAdvisorViewResolver } from './modules/application/shared/resolvers/application-advisor-view/application-advisor-view-resolver';
import { ApplicationCoordinationInitializerResolver } from './modules/application/shared/resolvers/application-coordination-initializer/application-coordination-initializer-resolver';
import { ApplicationCoordinationStatusResolver } from './modules/application/shared/resolvers/application-coordination-status/application-coordination-status-resolver';
import { ApplicationEntryInitializerResolver } from './modules/application/shared/resolvers/application-entry-initializer/application-entry-initializer-resolver';
import { ApplicationStateOrderStatusResolver } from './modules/application/shared/resolvers/application-order-status/application-state-order-status-resolver';
import { ApplicationPageAccessResolver } from './modules/application/shared/resolvers/application-page-access/application-page-access-resolver';
import { ApplicationStateInitializerResolver } from './modules/application/shared/resolvers/application-state-initializer/application-state-initializer-resolver';
import { CurrentStepResolver } from './modules/application/shared/resolvers/current-step/current-step-resolver';
import { DirectEntryFormInitializerResolver } from './modules/application/shared/resolvers/direct-entry/direct-entry-form-initializer-resolver';
import { FrnFilingInitializerResolver } from './modules/application/shared/resolvers/frn-filing/frn-filing-initializer-resolver';
import { UnsupportedApplicationResolver } from './modules/application/shared/resolvers/unsupported-application/unsupported-application-resolver';
import { ApplicationTemplateDashboardComponent } from './modules/application/template/components/application-template-dashboard/application-template-dashboard.component';
import { UnsupportedApplicationComponent } from './modules/application/unsupported/unsupported-application.component';
import { DashboardComponent } from './modules/dashboard/dashboard.component';
import { GoSsoRedirectComponent } from './modules/launch/go-sso-redirect/go-sso-redirect-component';
import { SsoRedirectComponent } from './modules/launch/sso-redirect/sso-redirect-component';
import { ReportsComponent } from './modules/reports/components/reports/reports.component';
import { ServiceFulfillmentContainerComponent } from './modules/service-fulfillment/service-fulfillment-container/service-fulfillment-container.component';
import { EditExecutorProfileComponent } from './modules/user/profile/edit-executor-profile/edit-executor-profile.component';
import { EditUserProfileComponent } from './modules/user/profile/edit-user-profile/edit-user-profile.component';
import { ViewExecutorProfileComponent } from './modules/user/profile/view-executor-profile/view-executor-profile.component';
import { RegistrationCompleteComponent } from './modules/user/registration/registration-complete/registration-complete.component';
import { RegistrationComponent } from './modules/user/registration/registration/registration.component';
import { UserReviewComponent } from './modules/user/review/user-review/user-review.component';
import { UserManagementComponent } from './modules/user/user-management/user-management.component';
import { ApplicationCoordinationLayoutComponent } from './shared/layouts/application-coordination-layout/application-coordination-layout.component';
import { ApplicationLayoutComponent } from './shared/layouts/application-layout/application-layout.component';
import { ApplicationPreparationLayoutComponent } from './shared/layouts/application-preparation-layout/application-preparation-layout.component';
import { BaseLayoutComponent } from './shared/layouts/base-layout/base-layout.component';
import { FrnFilingApplicationLayoutComponent } from './shared/layouts/frn-filing-application-layout/frn-filing-application-layout.component';
import { OrderDetailsLayoutComponent } from './shared/layouts/order-details-layout/order-details-layout.component';
import { PaymentDetailsLayoutComponent } from './shared/layouts/payment-details-layout/payment-details-layout.component';
import { SimpleLayoutComponent } from './shared/layouts/simple-layout/simple-layout.component';
import { UnsupportedApplicationLayoutComponent } from './shared/layouts/unsupported-application-layout/unsupported-application-layout.component';

export const routes: Routes = [
    {
        path: '',
        component: SimpleLayoutComponent,
        children: [
            {
                path: RouteConstants.SSO.rootUrl,
                component: SsoRedirectComponent,
                pathMatch: 'full',
            },
            {
                path: `${RouteConstants.SSO.rootUrl}/${RouteConstants.SSO.goSsoRedirect}`,
                component: GoSsoRedirectComponent,
                pathMatch: 'full',
            },
            {
                path: '',
                component: SsoRedirectComponent,
                pathMatch: 'full',
            },
            {
                path: 'registration',
                component: RegistrationComponent,
                canActivate: [AuthGuard],
            },
            {
                path: 'registration-complete',
                component: RegistrationCompleteComponent,
            },
            {
                path: 'signup',
                canActivate: [SignupGuard],
                children: [],
            },
        ],
    },
    {
        path: '',
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Dashboard.getRootUrl(),
                component: DashboardComponent,
            },
        ],
    },
    {
        path: '',
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Reports.getRootUrl(),
                component: ReportsComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.EwaStaffRoles,
                },
            },
        ],
    },
    {
        path: '',
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Order.rootUrl,
                component: OrderDashboardContainerComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanAccessOrders,
                },
            },
            {
                path: RouteConstants.Payment.rootUrl,
                component: CustomerPaymentDashboardComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]: [UserRole.Customer],
                },
            },
        ],
    },
    {
        path: '',
        component: OrderDetailsLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Order.rootUrlWithId,
                component: OrderDetailsComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [
                    BlockNavigationIfChangeGuard,
                    BlockNavigationIfPreferredPaymentMethodRequirementUnsatisfiedGuard,
                ],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanAccessOrders,
                },
            },
        ],
    },
    {
        path: '',
        component: PaymentDetailsLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Order.paymentDetails,
                component: PaymentDetailsComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanAccessPayments,
                },
            },
        ],
    },
    {
        path: '',
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Template.getRootUrl(),
                component: ApplicationTemplateDashboardComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
            },
        ],
    },
    {
        path: '',
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.ServiceFulfillment.rootUrl,
                component: ServiceFulfillmentContainerComponent,
                canActivate: [AuthenticatedUserStatusGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_STATUSES]: [
                        UserStatus.ACTIVE,
                    ],
                },
                resolve: {
                    ApplicationStateInitializerResolver,
                },
            },
        ],
    },
    {
        path: RouteConstants.Go.rootUrl,
        component: GoBaseLayoutComponent,
        canActivate: [...GoPageCanActivateGuards.CanActivate],
        children: [
            {
                path: RouteConstants.Go.purposeOfFiling,
                component: GoPurposeOfFilingComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
                resolve: {
                    CurrentStepResolver,
                },
            },
            {
                path: RouteConstants.Go.parameters,
                component: GoParametersComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
                resolve: {
                    CurrentStepResolver,
                },
            },
            {
                path: RouteConstants.Go.confirmParameters,
                component: GoConfirmParametersComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
                resolve: {
                    CurrentStepResolver,
                },
            },
        ],
    },
    {
        path: RouteConstants.Go.applicationRootUrl,
        component: GoBaseLayoutComponent,
        canActivate: [...GoPageCanActivateGuards.CanActivate],
        children: [
            {
                path: RouteConstants.Go.parametersWithApplicationId,
                component: GoParametersComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]: [UserRole.Customer],
                },
                resolve: { CurrentStepResolver },
            },
            {
                path: RouteConstants.Go.confirmParametersWithApplicationId,
                component: GoConfirmParametersComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]: [UserRole.Customer],
                },
                resolve: { CurrentStepResolver },
            },
            {
                path: RouteConstants.Go.searchProcessing,
                component: GoSearchProcessingComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]: [UserRole.Customer],
                },
                resolve: {
                    CurrentStepResolver,
                    GoPageApplicationStateRefreshResolver,
                },
            },
        ],
    },
    {
        path: RouteConstants.Go.applicationRootUrl,
        component: GoBaseCoordinationLayoutComponent,
        canActivate: [...GoPageCanActivateGuards.CanActivate],
        children: [
            {
                path: RouteConstants.Go.frequencyResults,
                component: GoFrequencyResultsComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]: [UserRole.Customer],
                },
                resolve: {
                    CurrentStepResolver,
                    GoPageApplicationHandedOverResolver,
                    GoPageApplicationStateRefreshResolver,
                    ApplicationCoordinationStatusResolver,
                },
            },
            {
                path: RouteConstants.Go.complete,
                component: GoCompleteComponent,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]: [UserRole.Customer],
                },
                resolve: {
                    CurrentStepResolver,
                    GoPageApplicationStateRefreshResolver,
                    ApplicationCoordinationStatusResolver,
                    ApplicationStateOrderStatusResolver,
                },
            },
        ],
    },
    {
        path: RouteConstants.Go.rootUrl,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Go.overview,
                component: GoOverviewComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
            },
            // For any other children, ensure HasVisitedGoOverviewGuard is added to enforce redirecting to Overview on first visit
            {
                path: RouteConstants.Go.help,
                component: GoHelpComponent,
                canActivate: [UserRoleAuthGuard, HasVisitedGoOverviewGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
            },
            {
                path: RouteConstants.Go.about,
                component: GoAboutComponent,
                canActivate: [UserRoleAuthGuard, HasVisitedGoOverviewGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
            },
            {
                path: RouteConstants.Go.fees,
                component: GoFeesComponent,
                canActivate: [UserRoleAuthGuard, HasVisitedGoOverviewGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                },
            },
        ],
    },
    {
        path: RouteConstants.Profile.getRootUrl(),
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.Profile.INDEX,
                component: ViewExecutorProfileComponent,
                canActivate: [AuthenticatedUserStatusGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_STATUSES]: [
                        UserStatus.ACTIVE,
                    ],
                },
            },
            {
                path: RouteConstants.Profile.EDIT,
                component: EditExecutorProfileComponent,
                canActivate: [AuthenticatedUserStatusGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_STATUSES]: [
                        UserStatus.ACTIVE,
                    ],
                },
            },
        ],
    },
    {
        path: RouteConstants.User.getRootUrl(),
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        children: [
            {
                path: RouteConstants.User.INDEX,
                component: UserManagementComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanManageAllUsers,
                },
            },
            {
                path: RouteConstants.User.REVIEW,
                canActivate: [UserRoleAuthGuard, UserStatusGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanManageAllUsers,

                    [GuardDataKeys.PERMITTED_USER_STATUSES]:
                        UserStatus.permittedUserStatusesForUserReview,
                },
                component: UserReviewComponent,
            },
            {
                path: RouteConstants.User.EDIT,
                canActivate: [UserRoleAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanManageAllUsers,
                },
                component: EditUserProfileComponent,
            },
        ],
    },
    {
        path: RouteConstants.Application.rootUrl,
        component: BaseLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        resolve: { ApplicationEntryInitializerResolver },
        children: [
            {
                path: RouteConstants.Application.contactLookup,
                component: ContactLookupComponent,
                canActivate: [UserRoleAuthGuard],
                data: {
                    [GuardDataKeys.PERMITTED_USER_ROLES]:
                        AccessConstants.RolesThatCanCreateApplicationsForOtherCustomers,
                },
            },
        ],
    },
    {
        path: RouteConstants.Application.rootUrl,
        component: ApplicationCoordinationLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        resolve: {
            ApplicationCoordinationInitializerResolver,
        },
        children: [
            {
                path: RouteConstants.Application.coordinationParameters,
                canActivate: [CoordinationAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                component: CoordinationParametersComponent,
                resolve: {
                    CurrentStepResolver,
                    ApplicationStateInitializerResolver,
                },
            },
            {
                path: RouteConstants.Application.coordinationResults,
                canActivate: [CoordinationAuthGuard],
                canDeactivate: [BlockNavigationIfChangeGuard],
                component: CoordinationResultsComponent,
                resolve: {
                    CurrentStepResolver,
                    ApplicationStateInitializerResolver,
                },
            },
        ],
    },
    {
        path: RouteConstants.Application.rootUrl,
        component: ApplicationPreparationLayoutComponent,
        canActivate: [GeneralAuthGuardFn],
        resolve: { ApplicationEntryInitializerResolver },
        children: [
            {
                path: RouteConstants.Application.purposeOfFiling,
                component: PurposeOfFilingComponent,
                resolve: {
                    CurrentStepResolver,
                    ApplicationStateInitializerResolver,
                },
            },
            {
                path: RouteConstants.Application.quickStart,
                component: QuickStartComponent,
                resolve: {
                    CurrentStepResolver,
                    ApplicationStateInitializerResolver,
                },
            },
        ],
    },
    {
        /**
         * ApplicationAdvisorViewResolver runs only once when the user navigates to the application to determine if it should be marked as viewed by the advisor.
         * It does not rerun for later navigation within the application, such as navigating to and from the FRN filing of an application.
         */
        path: '',
        canActivate: [GeneralAuthGuardFn],
        resolve: { ApplicationAdvisorViewResolver },
        children: [
            {
                path: RouteConstants.Application.rootUrlWithId,
                component: UnsupportedApplicationLayoutComponent,
                resolve: {
                    UnsupportedApplicationResolver,
                },
                children: [
                    {
                        path: RouteConstants.Application.contactRequest,
                        component: UnsupportedApplicationComponent,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        resolve: {
                            ApplicationPageAccessResolver,
                        },
                    },
                ],
            },
            {
                path: RouteConstants.Application.directEntryRootUrl,
                component: ApplicationLayoutComponent,
                resolve: {
                    DirectEntryFormInitializerResolver,
                },
                children: [
                    {
                        path: DirectEntryStepPaths.generalInfo,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: GeneralInfoComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.feesWaiversExemptions,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: FeesWaiversExemptionsComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.applicantInfo,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: ApplicantInfoComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.regulatoryInfo,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: RegulatoryInfoComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.certificationInfo,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: CertificationInfoComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.scheduleHAdditionalInformation,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: ScheduleHAdditionalInformationComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.schedulesDAndH,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: SchedulesDAndHComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.scheduleK,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: ScheduleKComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.attachmentsAndWaivers,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: AttachmentsAndWaiversComponent,
                        resolve: directEntryPageResolvers,
                    },
                    {
                        path: DirectEntryStepPaths.submitToEwa,
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        component: SubmitToEwaComponent,
                        resolve: directEntryPageResolvers,
                    },
                ],
            },
            {
                path: RouteConstants.FrnFiling.rootUrl,
                component: FrnFilingApplicationLayoutComponent,
                resolve: {
                    FrnFilingInitializerResolver,
                },
                children: [
                    {
                        path: RouteConstants.FrnFiling.newFiling,
                        component: FrnFilingTypeComponent,
                        canActivate: [UserRoleAuthGuard],
                        resolve: { ApplicationPageAccessResolver },
                        data: {
                            [GuardDataKeys.PERMITTED_USER_ROLES]:
                                AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                        },
                    },
                    {
                        path: RouteConstants.FrnFiling.typeOfFiling,
                        component: FrnFilingTypeComponent,
                        canActivate: [UserRoleAuthGuard],
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        resolve: { ApplicationPageAccessResolver },
                        data: {
                            [GuardDataKeys.PERMITTED_USER_ROLES]:
                                AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                        },
                    },
                    {
                        path: RouteConstants.FrnFiling.detail,
                        component: FrnFilingDetailComponent,
                        canActivate: [UserRoleAuthGuard],
                        canDeactivate: [BlockNavigationIfChangeGuard],
                        resolve: { ApplicationPageAccessResolver },
                        data: {
                            [GuardDataKeys.PERMITTED_USER_ROLES]:
                                AccessConstants.RolesThatCanCreateApplicationsAndTemplates,
                        },
                    },
                ],
            },
        ],
    },

    {
        // If the route got through all known routes and it still isn't recorgnized
        // then redirect the user back to the root URL for now. We may want a custom 404 page.
        path: '**',
        redirectTo: RouteConstants.Home.getHomeUrl(),
    },
];
